import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
  CreateRoleRequest,
  GetRolesByPartnerNameResponse,
  GetRolesListResponseWithPagination,
  RoleResponse,
  RoleWithPermissionsResponse,
} from '../api/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { getArrayApiErrors } from 'utils/get-array-api-errors';
import {
  createAdminRole,
  deleteAdminRole,
  deleteAdminRoles,
  getAdminRole,
  getRolesByPartnerName,
  getRolesList,
  updateAdminRole,
} from 'features/admin-roles/api';
import { adminRolesActions } from 'features/admin-roles/redux/admin-roles.actions';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extract-error-message.util';
import { trackEvent } from 'utils/amplitude.util';
import { AmplitudeEvents } from 'shared/enums/amplitude-events.enum';

function* getAdminRolesList({
  payload,
}: PayloadAction<{
  page: number;
  limit: number;
  selectedPermission?: number;
}>): Generator<
  CallEffect<AxiosResponse<GetRolesListResponseWithPagination>> | PutEffect,
  void,
  AxiosResponse<GetRolesListResponseWithPagination>
> {
  try {
    const response = yield call(getRolesList, payload);
    yield put(adminRolesActions.getAdminRolesListSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminRolesActions.getAdminRolesListFailure(getArrayApiErrors(error))
    );
  }
}

function* getAdminRolesByPartnerName({
  payload,
}: PayloadAction<{ partnerName?: string }>): Generator<
  CallEffect<AxiosResponse<GetRolesByPartnerNameResponse>> | PutEffect,
  void,
  AxiosResponse<GetRolesByPartnerNameResponse>
> {
  try {
    const response = yield call(getRolesByPartnerName, payload);
    yield put(
      adminRolesActions.getAdminRolesByPartnerNameSuccess(response.data)
    );
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminRolesActions.getAdminRolesByPartnerNameFailure(
        getArrayApiErrors(error)
      )
    );
  }
}

function* createRole(
  data: PayloadAction<CreateRoleRequest>
): Generator<
  CallEffect<AxiosResponse<RoleResponse>> | PutEffect,
  void,
  AxiosResponse<RoleResponse>
> {
  try {
    const response = yield call(createAdminRole, data.payload);
    trackEvent(AmplitudeEvents.CREATE_ROLE_SUBMIT_SUCCESS, {
      role_name: data.payload.name,
    });
    toast.success('Role created successfully');
    yield put(adminRolesActions.createRoleSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminRolesActions.createRoleFailure(getArrayApiErrors(error)));
  }
}

function* getRole({
  payload,
}: PayloadAction<{ roleId: number }>): Generator<
  CallEffect<AxiosResponse<RoleWithPermissionsResponse>> | PutEffect,
  void,
  AxiosResponse<RoleWithPermissionsResponse>
> {
  try {
    const response = yield call(getAdminRole, payload);
    yield put(adminRolesActions.getRoleSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminRolesActions.getRoleFailure(getArrayApiErrors(error)));
  }
}

function* updateRole(
  data: PayloadAction<CreateRoleRequest & { roleId: number }>
): Generator<
  CallEffect<AxiosResponse<RoleResponse>> | PutEffect,
  void,
  AxiosResponse<RoleResponse>
> {
  try {
    const response = yield call(updateAdminRole, data.payload);
    toast.success('Role updated successfully');
    yield put(adminRolesActions.updateRoleSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminRolesActions.updateRoleFailure(getArrayApiErrors(error)));
  }
}

function* deleteRole(
  data: PayloadAction<{ roleId: number }>
): Generator<CallEffect<number> | PutEffect, void, number> {
  try {
    const response = yield call(deleteAdminRole, data.payload);
    toast.success('Role deleted successfully');
    yield put(adminRolesActions.deleteRoleSuccess(response));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminRolesActions.deleteRoleFailure(getArrayApiErrors(error)));
  }
}

function* deleteRoles(
  data: PayloadAction<number[]>
): Generator<CallEffect<number[]> | PutEffect, void, number[]> {
  try {
    const response = yield call(deleteAdminRoles, data.payload);
    toast.success('Roles deleted successfully');
    yield put(adminRolesActions.deleteRolesSuccess(response));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminRolesActions.deleteRolesFailure(getArrayApiErrors(error)));
  }
}

function* getAdminRolesSaga() {
  yield takeLatest(
    adminRolesActions.getAdminRolesListRequest,
    getAdminRolesList
  );

  yield takeLatest(
    adminRolesActions.getAdminRolesByPartnerNameRequest,
    getAdminRolesByPartnerName
  );

  yield takeLatest(adminRolesActions.createRoleRequest, createRole);
  yield takeLatest(adminRolesActions.getRoleRequest, getRole);
  yield takeLatest(adminRolesActions.updateRoleRequest, updateRole);
  yield takeLatest(adminRolesActions.deleteRoleRequest, deleteRole);
  yield takeLatest(adminRolesActions.deleteRolesRequest, deleteRoles);
}

export default getAdminRolesSaga;
